// IntentAnalysisIntegrator.js
import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * Composant utilitaire qui intègre l'analyse sémantique à l'espace d'inspiration existant
 * Il sert de middleware entre les différentes phases du parcours utilisateur
 */
const IntentAnalysisIntegrator = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    // Vérifier si nous avons des résultats d'analyse sémantique
    if (location.state?.semanticAnalysis) {
      const { semanticAnalysis, optimizedQuery } = location.state;
      
      // Traiter les résultats d'analyse pour les adapter au format attendu par l'espace de recherche
      processAnalysisResults(semanticAnalysis, optimizedQuery);
    } else if (location.state?.objective) {
      // Si nous avons un objectif mais pas d'analyse, rediriger vers l'analyse d'intention
      redirectToIntentAnalysis(location.state.objective);
    }
  }, [location.state, navigate]);
  
  /**
   * Traite les résultats d'analyse sémantique pour les adapter au format attendu par l'espace de recherche
   * @param {Object} analysis - Résultats de l'analyse sémantique
   * @param {string} optimizedQuery - Requête optimisée (facultative)
   */
  const processAnalysisResults = (analysis, optimizedQuery) => {
    // Convertir les formats entre l'analyse sémantique et l'espace de recherche
    const searchConfig = {
      query: optimizedQuery,
      objective: analysis.primaryGoal,
      intention: analysis.intention,
      keyTerms: analysis.keyTerms,
      contentTypes: analysis.contentTypes,
      searchScope: analysis.searchScope,
      expectedDepth: analysis.expectedDepth,
      temporalAspect: analysis.temporalAspect,
      analyzed: true
    };
    
    // Dans un environnement réel, vous pourriez stocker cette configuration
    // dans un contexte React, Redux, ou une base de données
    
    // Pour cet exemple, nous la passons via l'état de la navigation
    const pathParts = location.pathname.split('/');
    const targetPath = pathParts[1] === 'webSource' ? location.pathname : '/webSource';
    
    navigate(targetPath, {
      state: {
        ...location.state,
        searchConfig
      },
      replace: true // Remplacer l'entrée actuelle dans l'historique
    });
  };
  
  /**
   * Redirige vers l'analyse d'intention avec l'objectif spécifié
   * @param {string} objective - Objectif de l'utilisateur
   */
  const redirectToIntentAnalysis = (objective) => {
    // Extraire l'ID de compétence de l'URL actuelle si présent
    const pathParts = location.pathname.split('/');
    const competenceId = pathParts.length > 2 ? pathParts[2] : null;
    
    navigate(`/intent-analysis${competenceId ? `/${competenceId}` : ''}`, {
      state: {
        objective,
        returnPath: location.pathname
      }
    });
  };
  
  // Ce composant ne rend rien visuellement, il agit comme un middleware
  return null;
};

export default IntentAnalysisIntegrator;